import { makeAutoObservable, runInAction } from 'mobx'
import { gql } from 'graphql-request'
import { authorizedGraphQLRequest } from '@/api'
import { Message } from './chatStore'

// Define GraphQL queries for chat history
const GET_CHAT_HISTORY = gql`
  query getChatHistory($limit: Int!, $offset: Int!) {
    getChatHistory(limit: $limit, offset: $offset) {
      _id
      user_id
      messages {
        content
        role
      }
      temperature
      max_tokens
      case_number
      language
      cost
      current_time
      # rating
    }
  }
`

// Define GraphQL query for user sessions
const GET_USER_SESSIONS = gql`
  query getUserSessions($limit: Int!, $offset: Int!, $sessionToken: String, $emailFilter: String) {
    getUserSessions(limit: $limit, offset: $offset, sessionToken: $sessionToken, emailFilter: $emailFilter) {
      id
      sessionToken
      appName
      messages {
        fold_name
        messages {
          content
          role
        }
      }
      startTime
      endTime
      createdAt
    }
  }
`

export interface ChatHistory {
  _id: string
  user_id: string
  name: string
  messages: Message[]
  temperature: number
  max_tokens: number
  case_number: number
  cost: number
  instructions: string
  current_time: string
  rating: number
  // duration: number
}

export interface SessionMessage {
  fold_name: string
  messages: Message[]
}

export interface UserSession {
  id: string
  sessionToken: string
  appName: string
  messages: SessionMessage[]
  startTime: string | null
  endTime: string | null
  createdAt: string
}

class ChatHistoryStore {
  chats: ChatHistory[] = []
  sessions: UserSession[] = []
  currentChat: ChatHistory | null = null
  currentSession: UserSession | null = null
  isLoading: boolean = false
  error: string | null = null
  hasMore: boolean = true
  offset: number = 0
  limit: number = 10
  georgiaOffset: number = 0
  emailFilter: string = ''
  searchValue: string = ''

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true })
  }

  setEmailFilter(email: string) {
    this.emailFilter = email
  }

  setSearchValue(value: string) {
    this.searchValue = value
  }

  /**
   * Fetch initial chat history
   */
  async fetchInitialChatHistory() {
    if (this.isLoading) return

    this.isLoading = true
    this.error = null
    this.offset = 0

    try {
      const data = await authorizedGraphQLRequest<{
        getChatHistory: ChatHistory[]
      }>(GET_CHAT_HISTORY, { limit: this.limit, offset: this.offset })

      runInAction(() => {
        this.chats = data.getChatHistory
        // this.hasMore = data.getChatHistory.hasMore
        this.offset = this.chats.length
      })
    } catch (err) {
      runInAction(() => {
        console.error('Failed to fetch chat history:', err)
        this.error = 'Failed to load chat history'
      })
    } finally {
      runInAction(() => {
        this.isLoading = false
      })
    }
  }

  /**
   * Fetch more chat history
   */
  async fetchMoreChatHistory() {
    if (this.isLoading || !this.hasMore) return

    this.isLoading = true
    this.error = null

    try {
      const data = await authorizedGraphQLRequest<{
        getChatHistory: ChatHistory[]
      }>(GET_CHAT_HISTORY, { limit: this.limit, offset: this.offset })

      runInAction(() => {
        this.chats = [...this.chats, ...data.getChatHistory]
        // this.hasMore = data.getChatHistory.hasMore
        this.offset = this.chats.length
      })
    } catch (err) {
      runInAction(() => {
        console.error('Failed to fetch more chat history:', err)
        this.error = 'Failed to load more chat history'
      })
    } finally {
      runInAction(() => {
        this.isLoading = false
      })
    }
  }

  /**
   * Format date string to display format
   */
  formatDate(dateString: string): string {
    if (!dateString) return ''
    // Only append 'Z' if the string lacks timezone info, to treat as UTC
    const hasTimezone = /[Zz]$|[+-]\d{2}:?\d{2}$/.test(dateString)
    const date = new Date(hasTimezone ? dateString : dateString + 'Z')
    if (isNaN(date.getTime())) return ''
    return date.toLocaleString(undefined, {
      dateStyle: 'short',
      timeStyle: 'short',
    })
  }

  /**
   * Format time string to display format
   */
  formatTime(dateString: string): string {
    const date = new Date(dateString)
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }

  /**
   * Format duration in seconds to display format (mm:ss)
   */
  formatDuration(durationInSeconds: number): string {
    const minutes = Math.floor(durationInSeconds / 60)
    const seconds = durationInSeconds % 60
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
  }

  /**
   * Fetch initial user sessions
   */
  async fetchInitialUserSessions() {
    if (this.isLoading) return

    this.isLoading = true
    this.error = null
    this.georgiaOffset = 0

    try {
      const variables: any = { limit: this.limit, offset: this.georgiaOffset }
      if (this.emailFilter && this.emailFilter.length > 2) {
        variables.emailFilter = this.emailFilter
      }

      const data = await authorizedGraphQLRequest<{
        getUserSessions: UserSession[]
      }>(GET_USER_SESSIONS, variables)

      runInAction(() => {
        this.sessions = data.getUserSessions
        this.georgiaOffset += this.limit
        this.hasMore = data.getUserSessions.length > 0
        this.offset = this.sessions.length
      })
    } catch (err) {
      runInAction(() => {
        console.error('Failed to fetch user sessions:', err)
        this.error = 'Failed to load user sessions'
      })
    } finally {
      runInAction(() => {
        this.isLoading = false
      })
    }
  }

  /**
   * Fetch more user sessions
   */
  async fetchMoreUserSessions() {
    if (this.isLoading || !this.hasMore) return

    this.isLoading = true
    this.error = null

    try {
      const variables: any = { limit: this.limit, offset: this.georgiaOffset }
      if (this.emailFilter && this.emailFilter.length > 2) {
        variables.emailFilter = this.emailFilter
      }

      const data = await authorizedGraphQLRequest<{
        getUserSessions: UserSession[]
      }>(GET_USER_SESSIONS, variables)

      runInAction(() => {
        this.sessions = [...this.sessions, ...data.getUserSessions]
        this.georgiaOffset += this.limit
        this.hasMore = data.getUserSessions.length > 0
        this.offset = this.sessions.length
      })
    } catch (err) {
      runInAction(() => {
        console.error('Failed to fetch more user sessions:', err)
        this.error = 'Failed to load more user sessions'
      })
    } finally {
      runInAction(() => {
        this.isLoading = false
      })
    }
  }

  /**
   * Get session by token
   */
  async getSessionByToken(sessionToken: string) {
    if (this.isLoading) return null

    this.isLoading = true
    this.error = null
    this.currentSession = null

    try {
      // First check if the session is already in our sessions array
      const existingSession = this.sessions.find((s) => s.sessionToken === sessionToken)

      if (existingSession) {
        runInAction(() => {
          this.currentSession = existingSession
        })
        return existingSession
      }

      // If not found, fetch from API with the specific sessionToken
      const variables: any = { limit: 1, offset: 0, sessionToken }
      if (this.emailFilter && this.emailFilter.length > 2) {
        variables.emailFilter = this.emailFilter
      }

      const data = await authorizedGraphQLRequest<{
        getUserSessions: UserSession[]
      }>(GET_USER_SESSIONS, variables)

      const session = data.getUserSessions[0]

      if (session) {
        runInAction(() => {
          this.currentSession = session
          // Also add to sessions array if not already there
          if (!this.sessions.some((s) => s.sessionToken === sessionToken)) {
            this.sessions.push(session)
          }
        })
        return session
      }

      // Session not found
      runInAction(() => {
        this.error = 'Session not found'
      })
      return null
    } catch (err) {
      runInAction(() => {
        console.error('Failed to fetch session by token:', err)
        this.error = 'Failed to load session details'
      })
      return null
    } finally {
      runInAction(() => {
        this.isLoading = false
      })
    }
  }

  reset() {
    this.chats = []
    this.sessions = []
    this.currentChat = null
    this.currentSession = null
    this.hasMore = true
    this.offset = 0
    this.georgiaOffset = 0
    this.error = null
  }
}

export const chatHistoryStore = new ChatHistoryStore()
